import crosstab from "./crosstab"
import { PHASES } from "./model"
import type {
  Phase,
  PhaseStatistics,
  ResponseGrid,
  ResultMatrix,
} from "./model"
import { i18n } from "@/i18n"

/**
 * Enough precision to analyse with, few enough digits that no reader
 * mistakes float noise (2.6999999999999997) for a meaningful figure.
 */
const round = (value: number) => Number(value.toFixed(4))

const statsFor = (stats: PhaseStatistics) => ({
  n: stats.n,
  mean: round(stats.mean),
  sd: round(stats.sd),
})

/**
 * Serialises the grid and its aggregates for handing to an AI.
 *
 * Deliberate choices, both about being unambiguous rather than pretty:
 *
 * - **Labels are always English**, whatever locale the operator is
 *   viewing in — a Spanish-labelled export would not be comparable with
 *   an English one, and the pipeline reads labels, not phase keys. The
 *   locale is pinned via getFixedT rather than read off the singleton,
 *   which is what leaked a locale across a server render in #129.
 * - **No colour bands.** They are banded against each profile's own
 *   extremes (docs/profiling-maths.md §5), so exporting them invites the
 *   reader to treat "red" as an absolute category. It is not.
 *
 * JSON rather than a table because explicit keys make axis confusion —
 * the way models actually misread matrices — structurally impossible,
 * and `checks` lets the reader verify what it received.
 */
export default function formatForAnalysis(
  matrix: ResultMatrix,
  responses: ResponseGrid,
): string {
  const t = i18n.getFixedT("en", "matrix")
  const label = (phase: Phase) => t(`phases.${phase}`)

  const axis = (
    sums: Record<Phase, number>,
    stats: Record<Phase, PhaseStatistics>,
  ) =>
    PHASES.map((phase, index) => ({
      index,
      phase,
      label: label(phase),
      total: sums[phase],
      ...statsFor(stats[phase]),
    }))

  const sumOfRowTotals = PHASES.reduce(
    (sum, phase) => sum + matrix.rowSums[phase],
    0,
  )
  const sumOfColumnTotals = PHASES.reduce(
    (sum, phase) => sum + matrix.colSums[phase],
    0,
  )

  return JSON.stringify(
    {
      format: "profiling.app result grid v1",
      instrument: {
        topics: 30,
        optionsPerTopic: 10,
        topicsPerPhase: 3,
        sliderRange: [-9, 9],
        scaledRange: [1, 19],
        scaling: "raw + 10, applied before every aggregation",
      },
      orientation:
        "rows = topic phase (the question's phase); columns = option phase (the chosen option's phase). Rows and columns are two independent partitions of the same 300 scaled answers, so their totals share one grand total.",
      cellFormula:
        "cell(r,c) = round(sqrt(rowTotal[r] * colTotal[c]) - grandMean), where grandMean = grandTotal / 10",
      statistics: {
        convention:
          "population moments (divide by n) — the 30 answers behind each total are the complete contributing set, not a sample",
        sd: "sqrt(sum((x - mean)^2) / n)",
        invariance:
          "sd is identical on the raw -9..9 and the scaled 1..19 values; only mean differs. mean is reported on the scaled values.",
        baseline:
          "n=30 per row and per column, n=300 for the grid: read a row's or column's sd against the grid's, not on its own.",
      },
      rows: axis(matrix.rowSums, matrix.rowStats),
      columns: axis(matrix.colSums, matrix.colStats),
      cells: matrix.cells.map((row) => row.map((cell) => cell.value)),
      // The answers themselves. Summaries cannot be un-summarised, and
      // the whole reason for shipping these is that a new statistic —
      // bimodality, say — can only be invented from data.
      rawResponses: {
        note: "The 300 answers as given, raw -9..9 (the +10 scaling above applies only to the matrix aggregates). Thirty rows of ten: three sets of ten topics, one topic per phase in each set, phases in canonical order down each set. Each row's ten values are the ten option phases in that same order.",
        columnOrder: PHASES.map(label),
        rows: responses.rows.map((row) => ({
          set: row.set,
          phase: row.phase,
          vector: label(row.phase),
          topic: row.topicSlug,
          values: row.values,
        })),
      },
      // Derived from rawResponses, included because it is the thing the
      // interference grid stands in for and comparing the two is the
      // point of having both.
      trueTrueMatrix: {
        note: "The raw crosstab: for each (row = topic phase, column = option phase), the sum of the three direct responses to it — one from each of that phase's three topics. Rows n, n+10 and n+20 of rawResponses summed element-wise. Only three values per cell, which is why the interference grid above exists; raw -9..9, so cells can be negative.",
        cells: crosstab(responses),
      },
      grid: { grandTotal: sumOfRowTotals, ...statsFor(matrix.gridStats) },
      checks: {
        sumOfRowTotals,
        sumOfColumnTotals,
        mustBeEqual: true,
      },
    },
    null,
    2,
  )
}
